import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  BasePageMetaResolver,
  CanonicalPageResolver,
  CmsService,
  PageDescriptionResolver,
  PageMetaResolver,
  PageRobotsMeta,
  PageRobotsResolver,
  PageTitleResolver,
  PageType,
  RoutingService
} from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { BaseStoreService, CategoryService, CustomPageLinkService } from '../services';

@Injectable({
  providedIn: 'root'
})
export class CustomCategoryPageMetaResolver
  extends PageMetaResolver
  implements PageTitleResolver, PageDescriptionResolver, CanonicalPageResolver, PageRobotsResolver
{
  private titleTag$: Observable<string>;
  private descriptionTag$: Observable<string>;

  constructor(
    private categoryService: CategoryService,
    private cmsService: CmsService,
    private translateService: TranslateService,
    private baseStoreService: BaseStoreService,
    private basePageMetaResolver: BasePageMetaResolver,
    private routingService: RoutingService,
    private readonly pageLinkService: CustomPageLinkService
  ) {
    super();
    this.pageType = PageType.CATEGORY_PAGE;
    this.titleTag$ = this.categoryService.getCurrentCategory().pipe(
      filter((cat) => !!cat && !!cat.metaTags.entry),
      map((cat) => {
        return cat?.metaTags?.entry.find((tag) => tag.key === 'PAGE_TITLE_TAG')?.value;
      }),
      switchMap((titleCat) =>
        combineLatest([
          titleCat
            ? of(titleCat)
            : this.cmsService.getCurrentPage().pipe(
              filter((p) => !!p),
              map((p) => (p['titleTag'] ? p['titleTag'] : p?.title))
            ),
          this.baseStoreService.getBaseStore(),
          this.routingService.getRouterState()
        ])
      ),
      map(([titleCat, baseStore, route]) => {
        let _titleCat = titleCat?.replace(/<siteBrand>/gi, (site) =>
          /[a-z]/.test(site)
            ? `${baseStore['brandSite']?.charAt(0).toUpperCase()}${baseStore['brandSite']?.toLowerCase().slice(1)}`
            : baseStore['brandSite']?.toUpperCase()
        );

        if (route.state.queryParams?.page) {
          const page = parseInt(route.state.queryParams.page);
          if (page > 1) {
            _titleCat = _titleCat?.replace(
              /<page>/gi,
              `${this.translateService.instant('SEO.page')} ${page}`
            );
          } else {
            _titleCat = _titleCat?.replace(/<page>/gi, '');
          }
        } else {
          _titleCat = _titleCat?.replace(/<page>/gi, '');
        }
        return _titleCat.replaceAll('  ', ' ');
      })
    );

    this.descriptionTag$ = this.categoryService.getCurrentCategory().pipe(
      filter((cat) => !!cat && !!cat.metaTags.entry),
      map((category) => {
        if (category.url?.includes('/csc/') && !category.subCategories) {
          return this.translateService.instant('categoryPage.metaSubCatSis.description', {
            categoryName: category.name
          });
        }
        return category?.metaTags?.entry.find((tag) => tag.key === 'PAGE_DESCRIPTION_TAG')?.value;
      }),
      switchMap((descriptionCat) =>
        combineLatest([
          descriptionCat
            ? of(descriptionCat)
            : this.cmsService.getCurrentPage().pipe(map((p) => p['descriptionTag'])),
          this.baseStoreService.getBaseStore(),
          this.routingService.getRouterState()
        ])
      ),
      map(([descriptionCat, baseStore, route]) => {
        if (route.state.queryParams?.page && parseInt(route.state.queryParams?.page) > 1) {
          return null;
        } else {
          return descriptionCat
            ?.toString()
            .replace(
              /<siteBrand>/gi,
              `${baseStore['brandSite']?.charAt(0).toUpperCase()}${baseStore['brandSite']?.toLowerCase().slice(1)}`
            );
        }
      })
    );
  }

  resolveTitle(): Observable<string> {
    return this.titleTag$;
  }

  resolveDescription(): Observable<string> {
    return this.descriptionTag$;
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.basePageMetaResolver.resolveRobots();
  }

  resolveCanonicalUrl(): Observable<string> {
    return this.categoryService.getCurrentCategory().pipe(
      switchMap((category) =>
        this.pageLinkService.getTranslatedCanonicalUrl({}, category?.url))
    );
  }
}
